import { getAllFactoringReSelling } from 'lib/api/reSell/factoring';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { formatDate, validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import MultiBuyBack from './components/MultiBuyBack';
import MultiOwnCollection from './components/MultiOwnCollection';
import MultiPaymentFundResell from './components/MultiPayment';
import MultiResellMain from './components/NewMultiResell';
import RegisterSelector from './components/RegisterSelector';
import ResellDetails from './components/ResellDetails';
import RevertResell from './components/RevertResell';

const headers: { key: string; label: string; type?: string }[] = [
  { key: 'id', label: 'Id', type: 'numeric-id' },
  { key: 'folio', label: 'Folio' },
  { key: 'businessName', label: 'Cliente' },
  { key: 'businessIdentifier', label: 'Rut cliente' },
  { key: 'stakeholderName', label: 'Deudor' },
  { key: 'fundName', label: 'Fondo' },
  // { key: 'fundId', label: 'Rut fondo' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'createdAt', label: 'Creación', type: 'date' },
  { key: 'dueDate', label: 'Vencimiento', type: 'date' },
  { key: 'cessionDate', label: 'Cedido', type: 'confirm-lights' },
  { key: 'status', label: 'Estado', type: 'status' },
];

const FactoringResells: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [resells, setResells] = useState<any>([]);
  const [openNewResell, setOpenNewResell] = useState(false);
  const [openMultiSales, setOpenMultiSales] = useState(false);
  const [edit, setEdit] = useState(false);
  const [selectedResellIds, setSelectedResellIds] = useState<string[]>([]);
  const [openMultiPayment, setOpenMultiPayment] = useState(false);
  const [openMultiBuyBack, setOpenMultiBuyBack] = useState(false);
  const [openMultiOwnCollection, setOpenMultiOwnCollection] = useState(false);
  const [openCreatePendingAccounts, setOpenCreatePendingAccounts] =
    useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [openRegisterSelector, setOpenRegisterSelector] = useState(false);
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [folio, setFolio] = useState('');
  const [factoringResellIdsForCallback, setFactoringResellIdsForCallback] =
    useState<string[]>([]);
  const [dateForCallback, setDateForCallback] = useState<any>(null);
  const [fundId, setFundId] = useState<string | null>(null);
  const [selectedFund, setSelectedFund] = useState<any>(null);
  const [status, setStatus] = useState('ACTIVE');
  const [openRevert, setOpenRevert] = useState(false);
  const [resellToRevert, setResellToRevert] = useState<any>(null);
  const [startCreatedAt, setStartCreatedAt] = useState<string | null>(null);
  const [endCreatedAt, setEndCreatedAt] = useState<string | null>(null);
  const [startDueDate, setStartDueDate] = useState<string | null>(null);
  const [endDueDate, setEndDueDate] = useState<string | null>(null);
  const statusOptions = [
    { label: 'Todas', value: 'ALL' },
    { label: 'Completada', value: 'COMPLETED' },
    { label: 'Activa', value: 'ACTIVE' },
    { label: 'Recomprada', value: 'REBUYED' },
  ];
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [selectedSortBy, setSelectedSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const generateQuery = ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    let query = '';
    if (status && status !== 'ALL') query += `status=${status}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (folio) query += `folio=${folio}&`;
    if (fundId) query += `fundId=${fundId}&`;
    if (startCreatedAt) query += `startCreatedAt=${startCreatedAt}&`;
    if (endCreatedAt) query += `endCreatedAt=${endCreatedAt}&`;
    if (startDueDate) query += `startDueDate=${startDueDate}&`;
    if (endDueDate) query += `endDueDate=${endDueDate}&`;
    if (selectedSortBy) query += `sortBy=${selectedSortBy}&`;
    if (sortOrder) query += `sortOrder=${sortOrder}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}&`;
    if (exportComplete) query += `all=true&`;
    return query;
  };
  const newResell = {
    rate: '',
    operationCostFixed: 0,
    operationCostVariable: 0,
    rateCost: 0,
    operationCostRateVariable: '',
    startDate: formatDate(new Date()),
    endDate: formatDate(new Date()),
    documentId: null,
    observation: '',
    fundId: null,
  };
  const [resell, setResell] = useState(newResell);
  const fetchResellings = async ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize, exportComplete });
      const response = await getAllFactoringReSelling(dispatch, query);
      if (!exportComplete) {
        setResells(response.data);
        setPagination(response.pagination);
      }
      setLoading(false);
      return response;
    } catch (error) {
      alert('Error al cargar los pagos');
      setLoading(false);
    }
  };

  const triggerSearch = async () => {
    setOpenNewResell(false);
    await fetchResellings({ page: 0, pageSize: PAGE_SIZE });
  };

  const handleDetails = async (id: string) => {
    const resellSearched = resells.find((resell: any) => resell.id === id);
    if (!resellSearched) return;
    setResell(resellSearched);
    setOpenNewResell(true);
    setEdit(true);
  };

  const callBackCreatePendingAccounts = async (
    factoringResellIds: string[],
    date: any,
  ) => {
    await fetchResellings({ page: 0, pageSize: PAGE_SIZE });
    setOpenCreatePendingAccounts(true);
    setFactoringResellIdsForCallback(factoringResellIds);
    setDateForCallback(date);
  };

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'Folio',
      value: folio,
      setter: setFolio,
    },
    {
      type: 'text',
      label: 'Rut cliente',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      type: 'text',
      label: 'Rut deudor',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      type: 'select',
      label: 'Estado',
      value: status,
      setter: setStatus,
      options: statusOptions,
    },
    {
      type: 'autocomplete',
      label: 'Fondo',
      value: fundId,
      key: 'fundName',
      setter: (value: string | null) => {
        setFundId(value);
      },
      selectedValue: selectedFund,
      setSelectedValue: (value: any) => {
        setSelectedFund(value);
        if (value?.object?.id) {
          setFundId(value.object.id);
        } else {
          setFundId(null);
        }
      },
    },
    {
      type: 'date',
      label: 'Fecha de creación',
      value: startCreatedAt,
      setter: setStartCreatedAt,
      dateKey: 'startCreatedAt',
      dateType: 'created',
      pairKey: 'endCreatedAt',
    },
    {
      type: 'date',
      label: 'Fecha de creación',
      value: endCreatedAt,
      setter: setEndCreatedAt,
      dateKey: 'endCreatedAt',
      dateType: 'created',
      pairKey: 'startCreatedAt',
    },
    {
      type: 'date',
      label: 'Fecha de vencimiento',
      value: startDueDate,
      setter: setStartDueDate,
      dateKey: 'startDueDate',
      dateType: 'due',
      pairKey: 'endDueDate',
    },
    {
      type: 'date',
      label: 'Fecha de vencimiento',
      value: endDueDate,
      setter: setEndDueDate,
      dateKey: 'endDueDate',
      dateType: 'due',
      pairKey: 'startDueDate',
    },
  ];

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={() => triggerSearch()}
          selectedSortBy={selectedSortBy}
          setSelectedSortBy={setSelectedSortBy}
          sortOptions={[
            { value: 'createdAt', label: 'Creación' },
            { value: 'dueDate', label: 'Vencimiento' },
          ]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          dispatch={dispatch}
        />
      ),
    },
    {
      action: () => setOpenRegisterSelector(true),
      name: 'Registrar',
      color: 'primary',
      disabled:
        !validatePermissions(user, 'financing_fundSale_new') &&
        !validatePermissions(user, 'financing_fundSale_new_payment'),
    },
  ];

  const cantSelectRow = (row: any) => {
    return row.status === 'Completada' || row.status === 'Recomprada';
  };

  useEffect(() => {
    void fetchResellings({ page: 0, pageSize: PAGE_SIZE });
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás todas las ventas a fondos."
        variant="body1"
        buttons={buttonsData}
      />
      <TableWithPagination
        selected={selectedResellIds}
        setSelected={setSelectedResellIds}
        cantSelectRow={cantSelectRow}
        dataHeaders={headers}
        data={resells}
        setData={setResells}
        rowsPerPageDefault={50}
        handleClickRow={handleDetails}
        exportToFile="xlsx"
        exportComplete
        searchData={fetchResellings}
        pagination={pagination}
        setPagination={setPagination}
        loading={loading}
        setLoading={setLoading}
        lastElement
      />
      {openNewResell && (
        <ResellDetails
          resell={resell}
          setResell={setResell}
          open={openNewResell}
          setOpen={setOpenNewResell}
          setUp={() => fetchResellings({ page: 0, pageSize: PAGE_SIZE })}
          edit={edit}
          triggerSearch={triggerSearch}
          setEdit={setEdit}
        />
      )}
      {openMultiSales && (
        <MultiResellMain
          open={openMultiSales}
          setOpen={setOpenMultiSales}
          setUp={fetchResellings}
          pagination={pagination}
        />
      )}
      {openMultiPayment && (
        <MultiPaymentFundResell
          open={openMultiPayment}
          setOpen={setOpenMultiPayment}
          setUp={fetchResellings}
          payments={payments}
          setPayments={setPayments}
          setSelected={setSelectedResellIds}
          pagination={pagination}
          callBackCreatePendingAccounts={callBackCreatePendingAccounts}
        />
      )}
      {openMultiBuyBack && (
        <MultiBuyBack
          open={openMultiBuyBack}
          setOpen={setOpenMultiBuyBack}
          setUp={fetchResellings}
          payments={payments}
          setPayments={setPayments}
          setSelected={setSelectedResellIds}
          pagination={pagination}
          callBackCreatePendingAccounts={callBackCreatePendingAccounts}
        />
      )}
      {/* Desactivado porque los saldos de mora como se concilian primero siempre seran 0 */}
      {/* {openCreatePendingAccounts && (
        <CreateArrearFundPendingAccounts
          open={openCreatePendingAccounts}
          setOpen={setOpenCreatePendingAccounts}
          factoringResellsIds={factoringResellIdsForCallback}
          date={dateForCallback}
        />
      )} */}
      {openRegisterSelector && (
        <RegisterSelector
          open={openRegisterSelector}
          setOpen={setOpenRegisterSelector}
          setOpenMultiPayment={setOpenMultiPayment}
          setOpenMultiCreateResell={setOpenMultiSales}
          resells={resells}
          selectedResellIds={selectedResellIds}
          setPayments={setPayments}
          setOpenMultiBuyBack={setOpenMultiBuyBack}
          setOpenMultiOwnCollection={setOpenMultiOwnCollection}
        />
      )}
      {openMultiOwnCollection && (
        <MultiOwnCollection
          open={openMultiOwnCollection}
          setOpen={setOpenMultiOwnCollection}
          payments={payments}
          setPayments={setPayments}
          setSelected={setSelectedResellIds}
          callBackCreatePendingAccounts={callBackCreatePendingAccounts}
        />
      )}
      {openRevert && (
        <RevertResell
          open={openRevert}
          setOpen={setOpenRevert}
          resellToRevert={resellToRevert}
          triggerSearch={triggerSearch}
        />
      )}
    </div>
  );
};

export default FactoringResells;
